import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, X } from "lucide-react";
import { useCreditPurchase } from "@/lib/hooks/use-credit-purchase";
import { useCredits } from "@/contexts/CreditsContext";
import { useBilling } from "@/lib/hooks/use-billing";
import { isLovablePreview } from "@/lib/utils/environment";
import CreditLimitWarningDialog from "@/components/shared/CreditLimitWarningDialog";

/** Override modes triggered from the hidden UI menu so the real banner shows
 *  in the full app chrome even when the workspace actually has credits. */
export type CreditBannerOverride = "low" | "out" | null;
export type CreditBannerPlanOverride = "free" | "tall" | "grande" | null;
const OVERRIDE_EVENT = "credit-banner-preview";
const OVERRIDE_KEY = "credit-banner-preview-mode";
const OVERRIDE_PLAN_KEY = "credit-banner-preview-plan";
const DISMISS_KEY = "credit-banner-dismissed";

export function triggerCreditBannerPreview(
  mode: CreditBannerOverride,
  plan: CreditBannerPlanOverride = null,
) {
  if (mode) sessionStorage.setItem(OVERRIDE_KEY, mode);
  else sessionStorage.removeItem(OVERRIDE_KEY);
  if (plan) sessionStorage.setItem(OVERRIDE_PLAN_KEY, plan);
  else sessionStorage.removeItem(OVERRIDE_PLAN_KEY);
  window.dispatchEvent(new CustomEvent(OVERRIDE_EVENT, { detail: { mode, plan } }));
}

function useCreditBannerOverride(): { mode: CreditBannerOverride; plan: CreditBannerPlanOverride } {
  const read = () => {
    if (typeof window === "undefined") return { mode: null, plan: null };
    const m = sessionStorage.getItem(OVERRIDE_KEY);
    const p = sessionStorage.getItem(OVERRIDE_PLAN_KEY);
    return {
      mode: (m === "low" || m === "out" ? m : null) as CreditBannerOverride,
      plan: (p === "free" || p === "tall" || p === "grande" ? p : null) as CreditBannerPlanOverride,
    };
  };
  const [state, setState] = useState(read);
  useEffect(() => {
    const handler = () => setState(read());
    window.addEventListener(OVERRIDE_EVENT, handler);
    return () => window.removeEventListener(OVERRIDE_EVENT, handler);
  }, []);
  return state;
}

/**
 * Global banner shown when the workspace is low on or out of credits.
 *
 * Persistent signal per the billing spec — the *blocking* purchase modal only
 * appears when a credit-spending action is attempted (handled at those call
 * sites); this banner is the always-on heads-up with a "Buy credits" shortcut.
 *
 * Dual-mode: legacy CreditsContext in Lovable preview, real billing data in
 * production. Renders nothing while billing is still loading or when the
 * workspace has no credit limit, so it never flashes on first paint.
 */
export default function CreditBalanceBanner() {
  const { t } = useTranslation();
  const preview = isLovablePreview();
  const legacy = useCredits();
  const { balance: realBalance, limit: realLimit, isLowCredits: realLow, loading } = useCreditPurchase();
  const { billingInfo } = useBilling();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { mode: overrideMode, plan: overridePlan } = useCreditBannerOverride();
  const [dismissedMode, setDismissedMode] = useState<string | null>(() =>
    typeof window === "undefined" ? null : sessionStorage.getItem(DISMISS_KEY),
  );

  const balance = preview ? legacy.balance : realBalance;
  const limit = preview ? legacy.monthlyAllowance : realLimit;
  void realLow;

  // Pessimistic: don't surface until we have real data, and only when there's
  // an actual limit to be low against. Hidden UI override bypasses these
  // gates so designers can preview the banner in the full app chrome.
  let mode: "low" | "out" | null = overrideMode;
  if (!mode) {
    if (!preview && loading) return null;
    if (limit <= 0) return null;
    if (balance <= 0) mode = "out";
    else if (limit > 0 && balance / limit <= 0.15) mode = "low";
  }
  if (!mode) return null;
  if (dismissedMode === mode && !overrideMode) return null;

  const planNameRaw = (billingInfo?.planName ?? (preview ? "Grande" : "Free")).toLowerCase();
  const plan: "free" | "tall" | "grande" =
    overridePlan ??
    (planNameRaw === "grande" ? "grande" : planNameRaw === "tall" ? "tall" : "free");
  const seats = billingInfo?.seats ?? (preview ? 5 : 1);

  // Feed the dialog the real recurring per-seat allotment so its "credits/seat ×
  // seats" breakdown and total match what the workspace actually has, rather than
  // the per-tier estimate. Falls back to the dialog's per-tier default when the
  // subscription limit isn't known.
  const subLimit = billingInfo?.subscriptionCreditsLimit ?? null;
  const creditsPerSeat = subLimit != null && seats > 0 ? Math.round(subLimit / seats) : undefined;
  const creditsUsed = billingInfo?.totalCreditsUsed ?? undefined;

  const isOut = mode === "out";
  const tone = isOut
    ? "border-destructive/30 bg-destructive/10"
    : "border-amber-500/30 bg-amber-500/10";
  const iconTone = isOut ? "text-destructive" : "text-amber-600 dark:text-amber-400";
  const linkTone = isOut
    ? "text-destructive"
    : "text-amber-700 dark:text-amber-300";

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setDialogOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setDialogOpen(true);
          }
        }}
        className={`relative w-full shrink-0 cursor-pointer border-b px-4 py-2 text-left ${tone}`}
      >
        <div className="flex items-center justify-center gap-3 text-xs text-foreground">
          <AlertCircle className={`h-3.5 w-3.5 shrink-0 ${iconTone}`} />
          {isOut ? (
            <p>
              <span className="font-semibold">{t("banners.creditsOutTitle")}</span>{" "}
              {t("banners.creditsOutBody")}
            </p>
          ) : (
            <p>
              <span className="font-semibold">{t("banners.creditsLowTitle")}</span>{" "}
              {t("banners.creditsLowBody")}
            </p>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDialogOpen(true);
            }}
            className={`shrink-0 font-semibold underline underline-offset-2 transition hover:opacity-80 ${linkTone}`}
          >
            {t("banners.creditsGetMore")}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (overrideMode) {
                triggerCreditBannerPreview(null);
              } else {
                sessionStorage.setItem(DISMISS_KEY, mode!);
                setDismissedMode(mode);
              }
            }}
            aria-label={t("banners.dismissBanner")}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-0.5 text-foreground/50 hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <CreditLimitWarningDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        plan={plan}
        seats={seats}
        creditsPerSeat={creditsPerSeat}
        creditsUsed={creditsUsed}
        severity={mode}
      />
    </>
  );
}

/** Preview wrappers for the hidden UI menu — render the banners inline so
 *  designers can see the styling, and expose triggers that activate the real
 *  banner across the full app chrome. */
export function CreditBalanceBannerPreview() {
  const { t } = useTranslation();
  const plans: Array<"free" | "tall" | "grande"> = ["free", "tall", "grande"];
  return (
    <div className="space-y-3">
      <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-2">
        <div className="flex items-center justify-center gap-3 text-xs text-foreground">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <p>
            <span className="font-semibold">{t("banners.creditsLowTitle")}</span>{" "}
            {t("banners.creditsLowBody")}
          </p>
        </div>
      </div>
      <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2">
        <div className="flex items-center justify-center gap-3 text-xs text-foreground">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
          <p>
            <span className="font-semibold">{t("banners.creditsOutTitle")}</span>{" "}
            {t("banners.creditsOutBody")}
          </p>
        </div>
      </div>
      <div className="space-y-2 pt-1">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {t("banners.creditsPreviewTrigger")}
        </p>
        <div className="grid grid-cols-[auto_1fr_1fr_1fr] items-center gap-2">
          <span className="text-xs text-muted-foreground">{t("banners.creditsPreviewLow")}</span>
          {plans.map(p => (
            <button
              key={`low-${p}`}
              type="button"
              onClick={() => triggerCreditBannerPreview("low", p)}
              className="inline-flex h-8 items-center justify-center rounded-md border border-input bg-background px-3 text-xs font-medium capitalize hover:bg-accent"
            >
              {p}
            </button>
          ))}
          <span className="text-xs text-muted-foreground">{t("banners.creditsPreviewOut")}</span>
          {plans.map(p => (
            <button
              key={`out-${p}`}
              type="button"
              onClick={() => triggerCreditBannerPreview("out", p)}
              className="inline-flex h-8 items-center justify-center rounded-md border border-input bg-background px-3 text-xs font-medium capitalize hover:bg-accent"
            >
              {p}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => triggerCreditBannerPreview(null)}
          className="inline-flex h-8 items-center rounded-md border border-input bg-background px-3 text-xs font-medium hover:bg-accent"
        >
          {t("banners.creditsPreviewClear")}
        </button>
      </div>
    </div>
  );
}
